import * as React from "react";
import { Panel, PanelType, Stack, DatePicker, Dropdown, IDropdownOption, Spinner, MessageBar, DetailsList, TooltipHost, Icon, MessageBarType, SelectionMode, IColumn, TextField, Checkbox, ContextualMenu, IContextualMenuProps, ActionButton, DefaultButton } from "office-ui-fabric-react";
import { loadLocations, getSites } from "../../../api/siteslocations";
import { ILocation, IRoom, ISite } from "../../../types/SiteLocation.types";
import { getRoomAvailability } from "../../../api/rooms";
import { ConstraintType, IAvailableRoomSlot, IBookingConstraint, IRoomAvailability } from "../../../types/Booking.types";
import { RoomBookingForm } from "../forms/RoomBookingForm";
import { ProfileContext } from "../../../Home";
import Utils from "../../../utils";

export interface IRoomBookingSearchProps {
}

const columns: IColumn[] = [
        {
            key: "name",
            fieldName: "Name",
            name: "Room",
            minWidth: 180,
            maxWidth: 200,
        },
        {
            key: "capacity",
            fieldName: "Capacity",
            name: "Capacity",
            minWidth: 80,
            maxWidth: 100,
        },
        {
            key: "equipment",
            fieldName: "Equipment",
            name: "Equipment",
            isIconOnly: true,
            iconName: "Info",
            minWidth: 30,
            maxWidth: 50,
            onRender: (item: IRoom) => {
                if (item.Equipment) {
                    return (
                        <TooltipHost tooltipProps={{ styles: { content: { whiteSpace: "pre-wrap" }}}} content={item.Equipment}>
                            <Icon iconName="Info" />
                        </TooltipHost>
                    )
                }
            }
        },
    ];

const initialFromDate = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0 ,0);
}

const initialToDate = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 17, 0 ,0);
}

export const RoomBookingSearch = (props:IRoomBookingSearchProps) => {

    const profileContext = React.useContext(ProfileContext);

    const [fromDate, setFromDate] = React.useState<Date>(initialFromDate());
    const [toDate, setToDate] = React.useState<Date>(initialToDate());
    const [slotFromDate, setSlotFromDate] = React.useState<Date|undefined>(undefined);
    const [slotToDate, setSlotToDate] = React.useState<Date|undefined>(undefined);
    const [sites, setSites] = React.useState<ISite[]>();
    const [siteOptions, setSiteOptions] = React.useState<IDropdownOption[]>([]);
    const [locations, setLocations] = React.useState<ILocation[]>();
    const [locationOptions, setLocationOptions] = React.useState<IDropdownOption[]>([]);
    const [selectedSite, setSelectedSite] = React.useState<number>();
    const [selectedLocation, setSelectedLocation] = React.useState<number>();
    const [availability, setAvailability] = React.useState<IAvailableRoomSlot[] | undefined>();
    const [availabilityError, setAvailabilityError] = React.useState<string>();
    const [resultsLoading, setResultsLoading] = React.useState(false);
    const [multiDay, setMultiDay] = React.useState(false);
    const [pickRoom, setPickRoom] = React.useState<number | undefined>();
    const [bookRoom, setBookRoom] = React.useState<IRoom>();

    // Runs on mount - loads a list of sites from the system
    const _loadSites = async (abortController: AbortController) => {
        const siteResponse = await getSites(abortController);
        if (siteResponse.ok) {
            const sites = await siteResponse.json() as ISite[];
            const siteDropdownOptions:IDropdownOption[] = sites.filter((s) => s.RoomBookingEnabled).map((s) => {
                return {
                    key: s.Id,
                    text: s.Title
                }
            });
            setSites(sites);
            setSiteOptions(siteDropdownOptions);
        }
    }

    // Runs when a new site is selected in the UI - loads a list of locations
    const _getLocations = React.useCallback(async (abortController: AbortController) => {
        if (selectedSite === undefined) return;
        setSelectedLocation(undefined);
        const locationResponse = await loadLocations(selectedSite);
        if (locationResponse.ok) {
            const locations = await locationResponse.json() as ILocation[];
            const locationDropdownOptions:IDropdownOption[] = locations.map((s) => {
                return {
                    key: s.Id,
                    text: s.Title
                }
            });
            setLocations(locations);
            setLocationOptions(locationDropdownOptions);
        }
    }, [selectedSite]);

    // When enough parameters are selected, this callback checks the room availability on a given date / time / site
    const _getAvailability = React.useCallback(async (abortController: AbortController) => {
        if (selectedSite && fromDate && toDate) {
            setResultsLoading(true);
            const response = await getRoomAvailability({
                fromDate, toDate, 
                locationId: selectedLocation,
                siteId: selectedSite,
            }, abortController);
            if (response.ok) {
                setAvailability(await response.json() as IAvailableRoomSlot[]);
            }
            setResultsLoading(false);
        }
    }, [selectedSite, selectedLocation, fromDate, toDate]);

    // This effect runs the above callback
    React.useEffect(() => {
        const abortController = new AbortController();
        _getAvailability(abortController);

        return () => {
            abortController.abort();
        };
    }, [_getAvailability]);

    // This effect runs when the selected site is changed
    React.useEffect(() => {
        const abortController = new AbortController();
        _getLocations(abortController);

        return () => {
            abortController.abort();
        };
    }, [_getLocations]);

    // On Mount - Load Sites
    React.useEffect(() => {
        const abortController = new AbortController();
        _loadSites(abortController);

        // If unmounting, cancel any async queries
        return () => {
            abortController.abort();
        };
    }, []);

    // Validate changes to date selections
    React.useEffect(() => {
        if (fromDate && toDate && fromDate > toDate) {
            setToDate(new Date(fromDate));
        }
        if (fromDate && toDate && toDate < fromDate) {
            const newDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), Math.max(fromDate.getHours(), toDate.getHours()), Math.max(fromDate.getMinutes(), toDate.getMinutes()), 0);
            setToDate(newDate);
        }
        if (`${toDate.getHours()}:${toDate.getMinutes()}` === `${fromDate.getHours()}:${fromDate.getMinutes()}`) {
            const newDate = new Date(toDate);
            newDate.setMinutes(newDate.getMinutes() + 15);
            setToDate(newDate);
        }
    }, [fromDate, toDate]);

    // on Time Change
    const _onTimeChange = React.useCallback((e: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const id = (e.target as HTMLDivElement).id;
        if (fromDate && toDate && option) {
            const itemKey = option.key as string;
            const timeElements = itemKey.split(":");
            if (timeElements && timeElements.length) {
                const hours = parseInt(timeElements[0], 10);
                const mins = parseInt(timeElements[1], 10);
                if (id === "fromTime") {
                    const newFromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), hours, mins);
                    setFromDate(newFromDate);
                } else {
                    const newToDate = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), hours, mins);
                    setToDate(newToDate);
                }
            }
        }
    }, [fromDate, toDate]);

    // If Desk Booking Time Constraint set globally, set maximum date on the date picker
    let maxDate: Date | undefined;
    let activeConstraint: IBookingConstraint | undefined = 
            locations?.find((l) => l.Id === selectedLocation)?.RoomBookingConstraint ||
            sites?.find((s) => s.Id === selectedSite)?.RoomBookingConstraint ||
            profileContext?.Settings.GlobalRoomConstraint;
    if (activeConstraint) {
        const num = activeConstraint.Number;
        const type = activeConstraint.Type as ConstraintType;
        if (num) {
            maxDate = new Date();
            switch (type) {
                case ConstraintType.Week:
                    maxDate.setDate(maxDate.getDate() + (7 * num));
                    break;
                case ConstraintType.Month:
                    maxDate.setMonth(maxDate.getMonth() + num);
                    break;
                case ConstraintType.Year:
                    maxDate.setFullYear(maxDate.getFullYear() + num);
                    break;
            }
        }
    }

    let selectedSiteInfo: JSX.Element | null = null;
    if (selectedSite) {
        const site = sites?.find((s) => s.Id === selectedSite);
        if (site && site.Notes) {
            selectedSiteInfo = <MessageBar styles={{root: { marginTop: 20}}} messageBarType={MessageBarType.info}>{site.Notes}</MessageBar>;
        }
    }

    let availableRooms: IAvailableRoomSlot[] = [];
    if (availability && availability.length > 0) {
        availableRooms = availability.reduce((prev, cur, idx, arr) => {
            if (!prev.find(slot => slot.ID === cur.ID)) {
                prev.push(cur);
            }
            return prev;
        }, [] as IAvailableRoomSlot[]);
    }

    let timeSlotChoices: IAvailableRoomSlot[] = [];
    if (pickRoom !== undefined && availability && availability.length) {
        timeSlotChoices = availability.filter(a => a.ID == pickRoom);
    }

    return (
        <>
            <Stack tokens={{ childrenGap: 10 }}>
                <Stack horizontal tokens={{childrenGap: 10}}> 
                    <DatePicker 
                        label={multiDay ? "From" : "Date"} 
                        minDate={new Date()}
                        maxDate={maxDate}
                        value={fromDate} 
                        onSelectDate={(date) => { 
                            if (date) {
                                const newFromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0);
                                setFromDate(newFromDate);
                                if (!multiDay) {
                                    const newToDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 17, 0);
                                    setToDate(newToDate);
                                } 
                            }
                        }} 
                    />
                    { multiDay === true && (
                        <DatePicker 
                            key={toDate?.getTime()}
                            label="To" 
                            value={toDate} 
                            minDate={new Date()}
                            maxDate={maxDate}
                            onSelectDate={(date) => { 
                                if (date) {
                                    let newDate = new Date(date);
                                    if (toDate) {
                                        newDate.setHours(toDate.getHours());
                                        newDate.setMinutes(toDate.getMinutes());
                                    }
                                    setToDate(newDate);
                                }
                            }}
                        />
                    )}
                    <Checkbox styles={{ root: { alignSelf: "flex-end", paddingBottom: 5}}} label="Date range" checked={multiDay} onChange={(e, checked) => setMultiDay(checked || false)} />
                </Stack>
                { Boolean(!fromDate) && Boolean(selectedSite) && (
                    <div style={{color: "darkred", fontSize: "0.8em"}}>Please select a date</div>
                )}
                { Boolean(fromDate) === true && (
                    <Stack horizontal tokens={{childrenGap: 10}}>
                        <Dropdown 
                            id="fromTime"
                            styles={{root: {minWidth: 100}}} 
                            label="From" 
                            options={[...Array(44)].map((val, idx) => ({
                                key: ("0" + (Math.floor(idx / 4) + 8)).slice(-2) + ":" + ("0" + (idx % 4 * 15)).slice(-2),
                                text: ("0" + (Math.floor(idx / 4) + 8)).slice(-2) + ":" + ("0" + (idx % 4 * 15)).slice(-2)
                            } as IDropdownOption))}
                            selectedKey={fromDate ? ("0" + fromDate.getHours()).slice(-2) + ":" + ("0" + fromDate.getMinutes()).slice(-2) : undefined}
                            onChange={_onTimeChange}
                        />
                        <Dropdown 
                            id="toTime"
                            styles={{root: {minWidth: 100}}} 
                            label="To" 
                            options={[...Array(45)].map((val, idx) => ({
                                key: ("0" + (Math.floor(idx / 4) + 8)).slice(-2) + ":" + ("0" + (idx % 4 * 15)).slice(-2),
                                text: ("0" + (Math.floor(idx / 4) + 8)).slice(-2) + ":" + ("0" + (idx % 4 * 15)).slice(-2)
                            } as IDropdownOption))}
                            selectedKey={toDate ? ("0" + toDate.getHours()).slice(-2) + ":" + ("0" + toDate.getMinutes()).slice(-2) : undefined}
                            onChange={_onTimeChange}
                        />
                    </Stack>
                )}
                <Stack horizontal horizontalAlign="stretch" tokens={{childrenGap: 10}}> 
                    <Dropdown styles={{root: { flexGrow: 1}}} label="Site" placeholder="Select a site" options={siteOptions} onChange={(event, option, index) => { if (option) setSelectedSite(option.key as number)}} />
                    <Dropdown styles={{root: { flexGrow: 1}}} label="Location" placeholder="Select a location" disabled={!selectedSite} options={locationOptions} onChange={(event, option, index) => { if (option) setSelectedLocation(option.key as number)}} />
                    {/* <Dropdown label="Desk" placeholder="Select a desk" disabled={!chosenSite} options={deskOptions} onChange={(event, option, index) => { if (option) setChosenDesk(option.key as number)}} /> */}
                </Stack>
            </Stack>
            { selectedSiteInfo }
            { resultsLoading === true && (
                <Spinner label="Loading results" />
            )}
            { (availableRooms.length > 0 || availabilityError != null) && (
                <>
                    <h2>Available Rooms</h2>
                    { availabilityError != null && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            No availability for the date selected
                        </MessageBar>
                    )}
                    { availableRooms.length > 0 && (
                        <DetailsList
                            items={availableRooms.sort(Utils.sortRoomInfoAlphabetically)}
                            columns={[...columns, {
                                key: "book",
                                name: "Action",
                                minWidth: 150,
                                onRender: (item: IAvailableRoomSlot, index?: number, column?: IColumn) => {
                                    return (
                                        <ActionButton styles={{flexContainer: { alignItems: "flex-start"}}} text="Request / Book" onClick={() => setPickRoom(item.ID)} />
                                    );
                                }
                            }]}
                            selectionMode={SelectionMode.none}
                        />
                    )}
                </>
            )}
            { timeSlotChoices.length > 0 && (
                <Panel
                    type={PanelType.medium}
                    headerText={timeSlotChoices[0].Name + ' - Choose a Date:'}
                    hidden={false}
                    isOpen={true}
                    onDismiss={() => setPickRoom(undefined)}
                >
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 20 }} style={{marginTop: 20}}>
                    {timeSlotChoices.map((slot) => {
                        const dateSt = new Date(slot.Start);
                        const dateEd = new Date(slot.Finish);
                        const startTime = Intl.DateTimeFormat('en-GB', { hour: "2-digit", minute: "2-digit", hour12: true }).format(dateSt);
                        const endTime = Intl.DateTimeFormat('en-GB', { hour: "2-digit", minute: "2-digit", hour12: true }).format(dateEd);
                        const dtStr = Intl.DateTimeFormat('en-GB', { weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(dateSt);
                        return (
                            <DefaultButton onClick={() => {
                                setSlotFromDate(dateSt);
                                setSlotToDate(dateEd);
                                setBookRoom({
                                    Id: slot.ID,
                                    LocationId: slot.LocationID,
                                    Name: slot.Name,
                                    Capacity: slot.Capacity,
                                    Equipment: slot.Equipment,
                                    Disabled: slot.Disabled,
                                    Hidden: slot.Hidden
                                } as IRoom);
                                setPickRoom(undefined);
                            }}>
                                <Stack style={{ minWidth: 350 }} horizontal horizontalAlign="space-between" tokens={{ childrenGap: 10 }}>
                                    <Stack style={{ flex: 1 }} horizontal tokens={{childrenGap: 10}}>
                                        <Icon iconName="Calendar" />
                                        <span>{dtStr}</span>
                                    </Stack>
                                    <Stack style={{ flex: 1 }} horizontal tokens={{ childrenGap: 10 }}>
                                        <Icon iconName="Clock" />
                                        <span>{`${startTime} - ${endTime}`}</span>
                                    </Stack>
                                </Stack>
                            </DefaultButton>
                        );
                    })}
                    </Stack>
                </Panel>
            )}
            {bookRoom !== undefined && fromDate !== undefined && toDate !== undefined && (
                <RoomBookingForm
                    roomId={bookRoom.Id}
                    start={slotFromDate || fromDate} finish={slotToDate || toDate}
                    roomInfo={bookRoom.Equipment}
                    onDismiss={(changesMade) => {
                        setBookRoom(undefined);
                        setSlotFromDate(undefined);
                        setSlotToDate(undefined);
                        if (changesMade) {
                            const abortController = new AbortController();
                            _getAvailability(abortController);
                        }
                    }} />
            )}
        </>
    )
};

export default RoomBookingSearch;